import React, { useState, useEffect } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Button, InputNumber } from 'antd';
import { EDIT_CAMPAIGN_SETTINGS } from 'api/campaignV2/mutations';
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignSettings,
  EditCampaignSettingsVariables,
} from 'api/campaignV2/types/EditCampaignSettings';
import { successNotification, errorNotification } from 'ui/Notification';
import styles from './ChangeInviteProcessingFee.module.scss';

type ChangeInviteProcessingFeeProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

const DEFAULT_FEE = 5;

const ChangeInviteProcessingFee = ({
  record,
  refetch,
}: ChangeInviteProcessingFeeProps): JSX.Element | null => {
  const currentFee = (record as any)?.inviteProcessingFee ?? DEFAULT_FEE;
  const [feeValue, setFeeValue] = useState<number | null>(currentFee);

  useEffect(() => {
    setFeeValue(currentFee);
  }, [currentFee]);

  const [editCampaignSettings, { loading }] = useMutation<
    EditCampaignSettings,
    EditCampaignSettingsVariables
  >(EDIT_CAMPAIGN_SETTINGS);

  const handleFeeChange = (value: number | null) => {
    if (value !== null && value >= 0 && value <= 100) {
      setFeeValue(value);
    }
  };

  const handleUpdateFee = async () => {
    if (record?.id) {
      try {
        const { data } = await editCampaignSettings({
          variables: {
            input: {
              campaignId: record.id,
              inviteProcessingFee: feeValue,
            } as any,
          },
        });
        if (data?.editCampaignSettings.id) {
          successNotification('Invite processing fee has been updated');
        }
        refetch();
      } catch (error: any) {
        console.error('Error updating invite processing fee', error);
        errorNotification(
          error?.message || 'Error updating invite processing fee'
        );
      }
    }
  };

  const hasChanged = feeValue !== currentFee;

  return (
    <div className={styles.root}>
      <p className={styles.label}>Invite Processing Fee</p>
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <InputNumber
            min={0}
            max={100}
            value={feeValue ?? undefined}
            onChange={(val) =>
              handleFeeChange(typeof val === 'number' ? val : null)
            }
            style={{ width: 80 }}
            placeholder="—"
          />
          <span className={styles.percentSign}>%</span>
        </div>

        <Button
          type="primary"
          onClick={handleUpdateFee}
          loading={loading}
          disabled={!hasChanged}
        >
          Update fee
        </Button>
      </div>
      <p className={styles.explanation}>
        Processing fee for talent invited via invite links. Falls back to global
        setting if empty.
      </p>
    </div>
  );
};

export default ChangeInviteProcessingFee;
